import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { environment } from '@env/environment';

import { MessageService } from '@core/shared/services/message.service';
import { FileUploadService } from '@core/shared/services/file-upload.service';

@Component({
    selector: 'cs-file-uploader-progress',
    templateUrl: 'file-uploader-progress.component.html',
    styles: [`.validation-msg {
        background: #d3d3d4;
    }
    
    .example-section {
        display: flex;
        align-content: center;
        align-items: center;
        height: 60px;
    }
    
    .example-margin {
        margin: 0 10px;
    }
    
    .progress-bar-value {
        position: relative;
        padding: 10px 0 10px 0;
        color: #545454;
        white-space: normal;
    }`]
})

export class FileUploaderProgressComponent implements OnChanges {
    @Input() uploadId: string;
    @Input() uploadStatus: number;
    @Output() changeUploadStatus: EventEmitter<string> = new EventEmitter<string>();
    @Output() onImportComplete = new EventEmitter();

    connection: any;
    proxy: any;
    statusHandlerRunning: boolean = false;
    validationSummary: any;
    rowsValidated: number;
    rowsToValidate: number;
    rowsImported: number;
    rowsToImport: number;
    fileStatusTypes: any = {
        readyForScan: 1,
        scanned: 2,
        saved: 3,
        validating: 4,
        validated: 5,
        importing: 6,
        imported: 7
    };

    constructor(private fileUploadService: FileUploadService, private messageService: MessageService) { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.uploadId && (changes.uploadId.previousValue != changes.uploadId.currentValue)) {
            this.progressUpload(changes.uploadId.currentValue);
        }
    }

    uploadStatusUpdateHandler(status) {
        if (status.status === "ValidationInProgress") {
            this.handleUploadStatus(this.fileStatusTypes.validating);
            this.rowsValidated = status.numberOfRowsProcessed;
            this.rowsToValidate = status.totalNumberOfRows;
            this.rowsImported = 0;
            this.rowsToImport = 0;
        }
        else if (status.status === "Validated") {
            this.fileValidated(status.validationResults);
        }
        else if (status.status === "ImportInProgress") {
            this.handleUploadStatus(this.fileStatusTypes.importing);
            this.rowsImported = status.numberOfRowsProcessed;
            this.rowsToImport = status.totalNumberOfRows;
        }
        else if (status.status === "Imported") {
            this.fileImported();
        }
        else if (status.status === "Error") {
            this.onFileUploadProcessingError(status.errorMessage);
        }
    }

    progressUpload(uploadId) {
        if (uploadId) {
            this.fileUploadService.progress(uploadId).subscribe((upload: any) => {
                if (upload.status === "Imported") {
                    return;
                }

                this.clearStatusHandler();
                this.setStatusHandlers();
                this.connection.start().done((data: any) => {
                    if (upload.status === "Validated") {
                        this.handleUploadStatus(this.fileStatusTypes.validated);
                        this.fileValidated(upload.validationSummary);
                    } else if (upload.status === "Uploaded") {
                        this.handleUploadStatus(this.fileStatusTypes.validating);
                        this.fileUploadService.validate(uploadId).subscribe(() => { });
                    } else if (upload.status === "ValidationInProgress") {
                        this.handleUploadStatus(this.fileStatusTypes.validating);
                    } else if (upload.status === "ImportInProgress") {
                        this.handleUploadStatus(this.fileStatusTypes.importing);
                    }
                }).catch((error: any) => {
                    console.log('Notification Hub error -> ' + error);
                });
            });
        }
    }

    setStatusHandlers() {
        if (!this.statusHandlerRunning) {
            this.connection = $.hubConnection(environment.baseUrl);
            this.proxy = this.connection.createHubProxy('fileUploadStatusUpdateHub');
            this.proxy.on('FileUploadStatusUpdate', (status) => this.uploadStatusUpdateHandler(status));
            this.statusHandlerRunning = true;
        }
    }

    clearStatusHandler() {
        if (this.statusHandlerRunning) {
            this.connection.stop();
            this.statusHandlerRunning = false;
        }
    }

    handleUploadStatus(status) {
        this.changeUploadStatus.emit(status);
    }

    fileImported() {
        //this.handleUploadStatus(this.fileStatusTypes.imported);
        this.onImportComplete.emit();
        this.messageService.showSuccess("File imported successfully");
        this.handleUploadStatus(this.fileStatusTypes.saved);
    }

    fileValidated(validationResultSummary) {
        this.handleUploadStatus(this.fileStatusTypes.validated);
        this.validationSummary = validationResultSummary;
    }

    onFileUploadProcessingError(uploadErrorMessage) {
        this.messageService.showError(uploadErrorMessage);
        this.handleUploadStatus(this.fileStatusTypes.saved);
        this.clearStatusHandler();
    }

    importCancelled() {
        this.handleUploadStatus(this.fileStatusTypes.saved);
        this.clearStatusHandler();
        this.fileUploadService.cancel(this.uploadId).subscribe(() => {
            this.messageService.showInfo("File upload process is cancelled");
        });
    }

    queueForImport() {
        this.handleUploadStatus(this.fileStatusTypes.importing);
        this.fileUploadService.import(this.uploadId).subscribe(() => { });
    }
}